// components/portal/BrandPricesTab.tsx
//
// What the brand charges in France, laid out as ChatGPT's shopping results: the brand's entry and
// typical price and where it sits in its trade, then one tile per reference product with its
// picture, price and the page it was read on (scripts/brands/brand-prices.ts). Every brand of a
// trade is priced on the same products, so two cards compare like for like.
import React, { useState } from 'react';
import { BrandMark } from '@/components/ui/BrandMark';
import { Stat } from '@/components/portal/BrandGoogleSummary';
import type { LeasingBrandDTO } from '#types/types';

const host = (url: string) => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; } };

// Product pages are the brand's own: a picture that will not load falls back to the brand's mark.
const ProductImage = ({ src, name, logoUrl }: { src: string | null; name: string; logoUrl?: string | null }) => {
  const [failed, setFailed] = useState(false);
  return src && !failed ? (
    <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} className="h-full w-full object-contain p-3 transition-transform duration-300 group-hover:scale-[1.03]" />
  ) : (
    <div className="flex h-full w-full items-center justify-center opacity-80"><BrandMark logoUrl={logoUrl ?? null} name={name} size={56} radius={10} /></div>
  );
};

export const BrandPricesTab = ({ brand, lang }: { brand: Partial<LeasingBrandDTO> | undefined; lang: string }) => {
  const fr = lang === 'fr';
  const prices = brand?.productPrices;
  const euros = (n: number) => n.toLocaleString(fr ? 'fr-FR' : 'en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: n < 20 && n % 1 ? 2 : 0 });
  const title = <h3 className="font-semibold text-[#ECECEC]" style={{ fontSize: 16, lineHeight: '24px' }}>{fr ? 'Prix en France' : 'Prices in France'}</h3>;

  if (!prices || (!prices.items.length && prices.entryPriceEur === null && prices.typicalPriceEur === null)) {
    return (
      <section>
        {title}
        <p className="mt-1 text-[#8E8E8E]" style={{ fontSize: 14 }}>{fr ? 'Aucun prix relevé sur le site de la marque pour l’instant.' : 'No price read on the brand’s own site yet.'}</p>
      </section>
    );
  }

  const index = prices.priceIndex;
  const position = index === null ? '–' : `${index.toLocaleString(fr ? 'fr-FR' : 'en-GB', { maximumFractionDigits: 1 })}×`;
  const checked = prices.checkedAt ? new Date(prices.checkedAt).toLocaleDateString(fr ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null;

  return (
    <div className="space-y-8">
      <section>
        {title}
        <p className="mt-0.5 text-[#8E8E8E]" style={{ fontSize: 13 }}>
          {[prices.activity, checked ? (fr ? `relevé le ${checked}` : `read on ${checked}`) : null].filter(Boolean).join(' · ')}
        </p>
        <div className="mt-3 flex gap-2">
          <Stat value={prices.entryPriceEur !== null ? euros(prices.entryPriceEur) : '–'} label={fr ? 'Prix d’entrée' : 'Entry price'} />
          <Stat value={prices.typicalPriceEur !== null ? euros(prices.typicalPriceEur) : '–'} label={fr ? 'Panier type' : 'Typical spend'} />
          <Stat value={position} label={fr ? 'Prix vs. médiane du secteur' : 'Price vs. trade median'} />
        </div>
      </section>
      {prices.items.length ? (
        <section>
          <h3 className="font-semibold text-[#ECECEC]" style={{ fontSize: 16, lineHeight: '24px' }}>{fr ? 'Produits de référence' : 'Reference products'}</h3>
          <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-5 @min-[560px]:grid-cols-3">
            {prices.items.map((item, i) => (
              <a key={`${i}:${item.url}`} href={item.url} target="_blank" rel="noopener noreferrer" className="group block min-w-0">
                <div className="aspect-square overflow-hidden rounded-[16px] bg-[#F4F4F4]">
                  <ProductImage src={item.image} name={brand?.name ?? ''} logoUrl={brand?.logoUrl} />
                </div>
                <div className="mt-2 line-clamp-2 text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }}>{item.product}</div>
                <div className="mt-0.5 font-medium text-[#ECECEC]" style={{ fontSize: 14 }}>{euros(item.priceEur)}</div>
                <div className="truncate text-[#8E8E8E] group-hover:text-[#AFAFAF]" style={{ fontSize: 13 }}>{host(item.url)}</div>
              </a>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};
